import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useMemo } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Grid, Stack } from '@mui/material'

import Accordion from '@/components/ui/accordions/Accordion'
import InfoSection from '@/components/ui/sections/InfoSection'
import TextInput from '@/components/ui/inputs/TextInput'
import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import DatePickerInput from '@/components/ui/inputs/DatePickerInput'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { calculateAge, getGenderOptions } from '@/utils/patient'

import { PatientProfileFormData } from '../model/usePatientProfileForm'

dayjs.extend(utc)

export function InsInfoSection({ editable, isQualified }: { editable: boolean; isQualified: boolean }) {
  useLingui()
  const isMobile = useIsMobile()
  const rhForm = useFormContext<PatientProfileFormData>()
  const [birthName, birthSurname, insNIR, insOID] = useWatch({ control: rhForm.control, name: ['birthName', 'birthSurname', 'insNIR', 'insOID'] })
  const { fields: insHistoryFields } = useFieldArray({ control: rhForm.control, name: 'insHistory' })

  const genderOptions = useMemo(getGenderOptions, [])

  return (
    <InfoSection label={t`INS information`}>
      <Grid container spacing={3} sx={{ mt: '0px' }}>
        {birthSurname && (
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`Last name at birth`} name="birthSurname" readOnly={isQualified || !editable} />
          </Grid>
        )}
        {birthName && (
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`First name at birth`} name="birthName" readOnly={isQualified || !editable} />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={birthSurname ? t`Usual last name` : t`Last name`}
            name="surname"
            readOnly={!editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController control={rhForm.control} fullWidth label={birthName ? t`Usual first name` : t`First name`} name="name" readOnly={!editable} />
        </Grid>
        <Grid item xs={isMobile ? 7 : 6}>
          <Controller
            control={rhForm.control}
            name="birthday"
            render={({ field }) => (
              <DatePickerInput
                disableFuture
                fullWidth
                name={field.name}
                label={t`Birthday`}
                onChange={(date) => {
                  rhForm.setValue('birthday', date ? dayjs.utc(date).toDate() : null)
                }}
                readOnly={isQualified || !editable}
                value={field.value && dayjs(field.value).isValid() ? dayjs.utc(field.value) : null}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            control={rhForm.control}
            name="birthday"
            render={({ field }) => <TextInput fullWidth label={t`Age`} name="age" value={field.value ? calculateAge(field.value) : ''} readOnly />}
          />
        </Grid>
        <Grid item xs={isMobile ? 5 : 6}>
          <SelectInputController
            control={rhForm.control}
            id="gender"
            fullWidth
            label={t`Gender`}
            name="gender"
            options={genderOptions}
            readOnly={isQualified || !editable}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextInputController
            control={rhForm.control}
            fullWidth
            label={t`Official geographic code`}
            name="birthPlaceCode"
            readOnly={isQualified || !editable}
          />
        </Grid>
        {insNIR.value && insNIR.key && (
          <Grid container item xs={12} spacing={3}>
            <Grid item xs={6}>
              <TextInputController control={rhForm.control} fullWidth label={t`INS registration number`} name="insNIR.value" readOnly />
            </Grid>
            <Grid item xs={6}>
              <TextInputController control={rhForm.control} fullWidth label={t`Key`} name="insNIR.key" readOnly />
            </Grid>
          </Grid>
        )}
        {insOID && (
          <Grid item xs={12} md={6}>
            <TextInputController control={rhForm.control} fullWidth label={t`OID`} name="insOID" readOnly />
          </Grid>
        )}
        {insHistoryFields.length > 0 && (
          <Grid item xs={12}>
            <Accordion title={t`Ins History`}>
              <Stack spacing={2}>
                {insHistoryFields.map((field, index) => (
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={6}>
                        <TextInputController
                          control={rhForm.control}
                          fullWidth
                          label={t`INS registration number`}
                          name={`insHistory.${index}.value`}
                          readOnly
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInputController control={rhForm.control} fullWidth label={t`Key`} name={`insHistory.${index}.key`} readOnly />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInputController control={rhForm.control} fullWidth label={t`Start date`} name={`insHistory.${index}.startDate`} readOnly />
                      </Grid>
                      <Grid item xs={6}>
                        <TextInputController control={rhForm.control} fullWidth label={t`End date`} name={`insHistory.${index}.endDate`} readOnly />
                      </Grid>
                    </Grid>
                  </Grid>
                ))}
              </Stack>
            </Accordion>
          </Grid>
        )}
      </Grid>
    </InfoSection>
  )
}
